import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMonsterTokenStyle } from '../utils/clientSettings';
import { getExpansionIcon } from '../utils/expansionArtwork';
import { observeElementResize } from './observeElementResize';

export type HeaderRoute =
  | 'home'
  | 'wiki'
  | 'achievements'
  | 'solo'
  | 'multiplayer'
  | 'game'
  | 'admin-games'
  | 'admin-performance'
  | 'settings'
  | 'about';

interface CommonHeaderProps {
  playerName: string;
  isGuestSession: boolean;
  activeRoute: HeaderRoute;
  onNavigateHome: () => void;
  onNavigateToWiki: () => void;
  onNavigateToAchievements: () => void;
  onNavigateToAbout: () => void;
  onOpenSettings: () => void;
  onOpenAccount: () => void;
  onOpenSignUp: () => void;
  onOpenLogin: () => void;
}

export const CommonHeader: React.FC<CommonHeaderProps> = ({
  playerName,
  isGuestSession,
  activeRoute,
  onNavigateHome,
  onNavigateToWiki,
  onNavigateToAchievements,
  onNavigateToAbout,
  onOpenSettings,
  onOpenAccount,
  onOpenSignUp,
  onOpenLogin,
}) => {
  const artwork = useMonsterTokenStyle();
  const [menuOpen, setMenuOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesMaxHeight, setRulesMaxHeight] = useState<number>();
  const header = useRef<HTMLElement>(null);
  const rulesDropdown = useRef<HTMLDivElement>(null);
  const rulesMenu = useRef<HTMLDivElement>(null);
  const rulesButton = useRef<HTMLButtonElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const hasMobileMenu = ['game', 'home', 'solo', 'multiplayer', 'wiki', 'achievements', 'about'].includes(activeRoute);
  useEffect(() => { setMenuOpen(false); setRulesOpen(false); }, [activeRoute]);
  useLayoutEffect(() => {
    if (!rulesOpen) return;
    const updateHeight = () => {
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      setRulesMaxHeight(Math.max(0, bottom - (rulesMenu.current?.getBoundingClientRect().top ?? 0) - 8));
    };
    updateHeight();
    const disconnect = observeElementResize(header.current, updateHeight);
    window.addEventListener('resize', updateHeight);
    window.addEventListener('scroll', updateHeight, true);
    window.visualViewport?.addEventListener('resize', updateHeight);
    window.visualViewport?.addEventListener('scroll', updateHeight);
    return () => {
      disconnect();
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('scroll', updateHeight, true);
      window.visualViewport?.removeEventListener('resize', updateHeight);
      window.visualViewport?.removeEventListener('scroll', updateHeight);
    };
  }, [rulesOpen, menuOpen]);
  useEffect(() => {
    if (!rulesOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!rulesDropdown.current?.contains(event.target as Node)) setRulesOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [rulesOpen]);
  const closeMenu = () => { setMenuOpen(false); setRulesOpen(false); };
  const isPlainLeftClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    return (
      event.button === 0
      && !event.metaKey
      && !event.altKey
      && !event.ctrlKey
      && !event.shiftKey
    );
  };

  const handleNavigateHome = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) {
      return;
    }
    event.preventDefault();
    closeMenu();
    if (activeRoute !== 'home') {
      onNavigateHome();
    }
  };

  const handleNavigateWiki = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) {
      return;
    }
    event.preventDefault();
    closeMenu();
    if (activeRoute !== 'wiki') {
      onNavigateToWiki();
    }
  };

  const handleNavigateAbout = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) {
      return;
    }
    event.preventDefault();
    closeMenu();
    if (activeRoute !== 'about') {
      onNavigateToAbout();
    }
  };

  const handleNavigateAchievements = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) {
      return;
    }
    event.preventDefault();
    closeMenu();
    if (activeRoute !== 'achievements') {
      onNavigateToAchievements();
    }
  };

  const handleOpenAccount = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) {
      return;
    }
    event.preventDefault();
    closeMenu();
    onOpenAccount();
  };

  return (
    <header ref={header} className={`common-header ${activeRoute === 'home' ? 'common-header-home' : ''} ${hasMobileMenu ? 'common-header-collapsible' : ''} ${menuOpen ? 'common-header-menu-open' : ''}`} onKeyDown={event => {
      if (event.key === 'Escape' && rulesOpen) {
        event.preventDefault();
        event.stopPropagation();
        setRulesOpen(false);
        rulesButton.current?.focus();
      } else if (event.key === 'Escape' && menuOpen) {
        event.preventDefault();
        event.stopPropagation();
        closeMenu();
        menuButton.current?.focus();
      }
    }}>
      <a className="common-header-brand" onClick={handleNavigateHome} href='/'>Arkham&nbsp;Horror</a>
      {hasMobileMenu && <button ref={menuButton} type="button" className="common-header-toggle" aria-label="Menu" aria-expanded={menuOpen} aria-controls="common-header-menu" onClick={() => { setMenuOpen(open => !open); setRulesOpen(false); }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
      </button>}
      <div className="common-header-menu" id="common-header-menu">
      <div className="common-header-nav">
        <a
          href="/"
          className={`common-header-link ${activeRoute === 'home' ? 'active' : ''}`}
          onClick={handleNavigateHome}
          aria-current={activeRoute === 'home' ? 'page' : undefined}
        >
          Main
        </a>
        <a
          href="/wiki"
          className={`common-header-link ${activeRoute === 'wiki' ? 'active' : ''}`}
          onClick={handleNavigateWiki}
          aria-current={activeRoute === 'wiki' ? 'page' : undefined}
        >
          Wiki
        </a>
        <a
          href="/achievements"
          className={`common-header-link ${activeRoute === 'achievements' ? 'active' : ''}`}
          onClick={handleNavigateAchievements}
          aria-current={activeRoute === 'achievements' ? 'page' : undefined}
        >
          Achievements
        </a>
        <div ref={rulesDropdown} className="rules-dropdown" onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setRulesOpen(false);
        }}>
          <button
            ref={rulesButton}
            type="button"
            className="common-header-link rules-dropdown-trigger"
            aria-expanded={rulesOpen}
            aria-controls="header-rules-links"
            onClick={() => setRulesOpen(open => !open)}
          >
            Rules
          </button>
          <div ref={rulesMenu} id="header-rules-links" className="rules-dropdown-menu" hidden={!rulesOpen} style={{ maxHeight: rulesMaxHeight }} onClick={closeMenu}>
            <a href="/rules/AH_Base_Rules.pdf" target="_blank" rel="noopener noreferrer">
              Base
            </a>
            <a href="/rules/AH_Dark_Pharaoh.pdf" target="_blank" rel="noopener noreferrer">
              Curse of the Dark Pharaoh
              <img className="rules-expansion-icon" src={getExpansionIcon('DarkPharaoh', artwork)} alt="" />
            </a>
            <a href="/rules/AH_Dunwich_Horror_Rules.pdf" target="_blank" rel="noopener noreferrer">
              Dunwich
              <img className="rules-expansion-icon" src={getExpansionIcon('Dunwich', artwork)} alt="" />
            </a>
            <a href="/rules/AH_kingsport_horror_rules.pdf" target="_blank" rel="noopener noreferrer">
              Kingsport
              <img className="rules-expansion-icon" src={getExpansionIcon('Kingsport', artwork)} alt="" />
            </a>
            <a href="/rules/AH_King_in_Yellow.pdf" target="_blank" rel="noopener noreferrer">
              The King in Yellow
              <img className="rules-expansion-icon" src={getExpansionIcon('KingInYellow', artwork)} alt="" />
            </a>
            <a href="/rules/AH_Black_Goat_Rules.pdf" target="_blank" rel="noopener noreferrer">
              The Black Goat of the Woods
              <img className="rules-expansion-icon" src={getExpansionIcon('BlackGoat', artwork)} alt="" />
            </a>
            <a href="/rules/AH_Insmouth_Rules.pdf" target="_blank" rel="noopener noreferrer">
              Innsmouth
              <img className="rules-expansion-icon" src={getExpansionIcon('Innsmouth', artwork)} alt="" />
            </a>
            <a href="/rules/AH_Lurker_Rules.pdf" target="_blank" rel="noopener noreferrer">
              The Lurker at the Threshold
              <img className="rules-expansion-icon" src={getExpansionIcon('Lurker', artwork)} alt="" />
            </a>
            <a href="/rules/AH_Faq.pdf" target="_blank" rel="noopener noreferrer">
              FAQ
            </a>
          </div>
        </div>
        <a
          href="/about"
          className={`common-header-link ${activeRoute === 'about' ? 'active' : ''}`}
          onClick={handleNavigateAbout}
          aria-current={activeRoute === 'about' ? 'page' : undefined}
        >
          About
        </a>
      </div>

      {!!playerName && !isGuestSession ? (
        <div className="header-account-controls">
          <button type="button" className="header-outline-button header-settings-button" onClick={() => { closeMenu(); onOpenSettings(); }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="m9 3-.6 2.4-2 .9L4.2 5.6 2 9.4l1.8 1.7v1.8L2 14.6l2.2 3.8 2.2-.7 2 .9L9 21h6l.6-2.4 2-.9 2.2.7 2.2-3.8-1.8-1.7v-1.8L22 9.4l-2.2-3.8-2.2.7-2-.9L15 3Z" /><circle cx="12" cy="12" r="3" /></svg>
            Settings
          </button>
          <a
            href="/settings"
            className="auth-status-link header-outline-button"
            onClick={handleOpenAccount}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false"><circle cx="12" cy="7" r="4" /><path d="M4 21v-2a8 8 0 0 1 16 0v2" /></svg>
            <span className="auth-status-link-name">
              Logged in as {playerName || 'Player'}
            </span>
          </a>
        </div>
      ) : (
        <div className="guest-status">
          <span className="auth-status-link-name">
            Guest
          </span>
          <button type="button" className='common-header-link' onClick={() => { closeMenu(); onOpenLogin(); }}>Login</button>
          <button type="button" className='common-header-link' onClick={() => { closeMenu(); onOpenSignUp(); }}>Sign Up</button>
        </div>
      )}
      </div>
    </header>
  );
};
